/**
 * useSalesOrdersSync — cross-page refresh bus for sales orders.
 *
 * Any page that mutates sales_orders should call:
 *     notifySalesOrdersChanged()
 * Every page that displays sales_orders should use:
 *     useSalesOrdersSync(() => fetchData())
 *
 * This keeps the Sales Kanban and Sales Pipeline in sync without websockets
 * or full Redux: a single browser-scoped CustomEvent + a 30-second poll fallback.
 */
import { useEffect } from 'react';

const EVENT_NAME = 'ti:sales-orders-changed';

export function notifySalesOrdersChanged() {
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function useSalesOrdersSync(refetch, { pollMs = 30000 } = {}) {
  useEffect(() => {
    const handler = () => { refetch(); };
    window.addEventListener(EVENT_NAME, handler);
    // Also refetch when tab regains focus
    const visHandler = () => { if (document.visibilityState === 'visible') refetch(); };
    document.addEventListener('visibilitychange', visHandler);
    // Polling fallback (short) so two pages in two tabs still converge
    let poll;
    if (pollMs > 0) poll = setInterval(refetch, pollMs);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      document.removeEventListener('visibilitychange', visHandler);
      if (poll) clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
